import { useForm } from "@tanstack/react-form";
import { InputLabel, InputContainer, InputDate } from "@sellar-io/components";
import { BasicDialog } from "../../../../components/modal/BasicDialog";
import { Alert } from "../../../../components/alert/Alert";
import { useSellarDistributionPalletFinalise } from "../../hooks/useSellarDistributionPalletFinalise";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import dayjs from "dayjs";

interface DistributionFinaliseDialogProps {
  palletShipmentId: number;
  active: boolean;
  onDismiss: () => void;
}

export const DistributionFinaliseDialog = ({
  palletShipmentId,
  active,
  onDismiss,
}: DistributionFinaliseDialogProps) => {
  const finalisePalletMutation =
    useSellarDistributionPalletFinalise(palletShipmentId);

  const form = useForm({
    defaultValues: {
      dispatchPlannedAt: new Date(dayjs().format("YYYY-MM-DD")),
      deliveryPlannedAt: new Date(dayjs().add(2, "days").format("YYYY-MM-DD")),
    },
    onSubmit: async ({ value }) => {
      await finalisePalletMutation.mutateAsync(value, {
        onSuccess: () => onDismiss(),
      });
    },
  });

  const errorMessage = finalisePalletMutation.error
    ? (apiV2AssertError(finalisePalletMutation.error)?.response?.data.message ??
      "An unknown error occurred")
    : null;

  return (
    <BasicDialog
      dialogActive={active}
      onDismiss={onDismiss}
      onConfirm={() => form.handleSubmit()}
      title="Finalise Pallet Shipment"
      description="Are you sure you want to finalise this pallet shipment? This will lock the pallet and process all associated orders in the background. All order dispatch planned and delivery planned dates will be updated."
      confirmButtonText="Finalise"
      dismissButtonText="Cancel"
      loading={finalisePalletMutation.isPending}
    >
      {errorMessage && (
        <Alert type="error" message={errorMessage} className="mb-4" />
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <div className="space-y-4">
          <div>
            <InputLabel>Dispatch Planned Date</InputLabel>
            <InputContainer>
              <form.Field name="dispatchPlannedAt">
                {(field) => (
                  <InputDate
                    type="date"
                    name={field.name}
                    value={dayjs(field.state.value).format("YYYY-MM-DD")}
                    onChange={(e) =>
                      field.handleChange(new Date(e.target.value))
                    }
                    min={dayjs().startOf("day").format("YYYY-MM-DD")}
                    required
                  />
                )}
              </form.Field>
            </InputContainer>
          </div>
          <div>
            <InputLabel>Delivery Planned Date</InputLabel>
            <InputContainer>
              <form.Field name="deliveryPlannedAt">
                {(field) => (
                  <InputDate
                    type="date"
                    name={field.name}
                    value={dayjs(field.state.value).format("YYYY-MM-DD")}
                    onChange={(e) =>
                      field.handleChange(new Date(e.target.value))
                    }
                    min={dayjs().startOf("day").format("YYYY-MM-DD")}
                    required
                  />
                )}
              </form.Field>
            </InputContainer>
          </div>
        </div>
      </form>
    </BasicDialog>
  );
};
